import { useEffect, useState } from "react";
import {
  getConsentSnapshot,
  getConversionTarget,
  isDebugModeEnabled,
  subscribeTrackingLog,
  type TrackingLogEntry,
} from "@/utils/googleAnalytics";

/**
 * Painel de verificação do rastreamento.
 * Só aparece quando o site é aberto com ?ga_debug=1 no endereço — nunca para
 * o visitante comum. Serve para o responsável pelo site confirmar, na hora,
 * se o clique no WhatsApp realmente enviou a conversão ao Google Ads.
 */
export default function TrackingDebugPanel() {
  const [entries, setEntries] = useState<TrackingLogEntry[]>([]);
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (!isDebugModeEnabled()) {
      return;
    }
    setVisible(true);
    return subscribeTrackingLog(setEntries);
  }, []);

  if (!visible) {
    return null;
  }

  const consent = getConsentSnapshot();

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-3 left-3 z-50 px-3 py-2 rounded-md bg-foreground-950 text-background-50 text-[11px] font-bold cursor-pointer"
      >
        <i className="ri-radar-line mr-1" />
        Rastreamento
      </button>
    );
  }

  return (
    <div className="fixed bottom-3 left-3 z-50 w-[300px] max-w-[calc(100vw-1.5rem)] rounded-lg border border-background-300/70 bg-background-50/98 backdrop-blur-sm text-[11px] leading-snug">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-background-200/70">
        <span className="font-bold text-foreground-950 flex items-center gap-1.5">
          <i className="ri-radar-line text-accent-600" />
          Verificação de rastreamento
        </span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Fechar painel de verificação"
          className="w-5 h-5 flex items-center justify-center rounded-sm text-foreground-600 hover:bg-background-200/70 cursor-pointer"
        >
          <i className="ri-close-line text-xs" />
        </button>
      </div>

      <div className="px-3 py-2 space-y-1 border-b border-background-200/70">
        <div className="flex items-center justify-between gap-2">
          <span className="text-foreground-600">Conversão do Ads</span>
          <span className="font-mono text-[10px] text-foreground-900 truncate">
            {getConversionTarget()}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-foreground-600">Cookies de anúncio</span>
          <span
            className={
              consent.adsGranted
                ? "font-bold text-primary-700"
                : "font-bold text-accent-700"
            }
          >
            {consent.adsGranted ? "liberado" : "bloqueado"}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-foreground-600">Cookies de análise</span>
          <span
            className={
              consent.analyticsGranted
                ? "font-bold text-primary-700"
                : "font-bold text-accent-700"
            }
          >
            {consent.analyticsGranted ? "liberado" : "bloqueado"}
          </span>
        </div>
      </div>

      <div className="max-h-[190px] overflow-y-auto px-3 py-2 space-y-1.5">
        {entries.length === 0 ? (
          <p className="text-foreground-600">
            Nenhum evento ainda. Navegue pelo site ou clique no WhatsApp.
          </p>
        ) : (
          entries.map((entry) => (
            <div key={entry.id} className="border-b border-background-200/60 last:border-0 pb-1.5 last:pb-0">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-semibold text-foreground-950">{entry.name}</span>
                <span className="text-[10px] text-foreground-500 shrink-0">{entry.time}</span>
              </div>
              <p className="text-[10px] text-foreground-600 break-all">{entry.detail}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}